import paper from 'paper';

import { changeZoom, getZoomFactorForWheelDelta, normalizeWheelDeltaY } from '../SimplePanAndZoom';
import registerLayerEvents, { LayerEventHandlers } from './paperLayerEvents';

let isPaperSetup = false;
let canvas: HTMLCanvasElement;

// This method needs to be called before anything is done with Paper.js
export function setupPaperJs(
  canvasElement: HTMLCanvasElement,
  layerEventHandlers: LayerEventHandlers,
  isZoomEnabled: () => boolean,
) {
  if (!isPaperSetup) {
    canvas = canvasElement;
    isPaperSetup = true;
    paper.setup(canvas);

    registerCanvasEvents(isZoomEnabled);
    registerLayerEvents(new paper.Layer(), layerEventHandlers);
  }
}

function registerCanvasEvents(isZoomEnabled: () => boolean) {
  // Zoom on mousewheel
  canvas.addEventListener('wheel', (event: WheelEvent) => {
    event.stopPropagation();
    event.preventDefault();

    if (!isZoomEnabled()) return;

    const deltaY = normalizeWheelDeltaY(event.deltaY, event.deltaMode);
    if (deltaY === 0) return;

    const mousePosition = new paper.Point(event.offsetX, event.offsetY);
    const viewPosition = paper.view.viewToProject(mousePosition);
    const zoomFactor = getZoomFactorForWheelDelta(deltaY, 1.1);
    const [newZoom, offset] = changeZoom(paper.view.zoom, zoomFactor, paper.view.center, viewPosition);

    paper.view.zoom = newZoom;
    paper.view.center = paper.view.center.add(offset!);
  });

  // Construct a paper.js tool but don't assign it to anything.  It will be accessed via paper.tool.
  new paper.Tool();

  let startDragPoint: paper.Point;

  paper.tool.onMouseDown = (event: paper.MouseEvent) => {
    (document.activeElement! as HTMLElement).blur();
    startDragPoint = event.point;
  };

  // Show box indicating the area that has been selected
  // For some reason this doesn't work properly in paper.project.activeLayer.onMouseDrag
  paper.tool.onMouseDrag = (event: paper.MouseEvent) => {
    // Must be a modifier
    if (event.modifiers.control || event.modifiers.shift) {
      const showSelection = new paper.Path.Rectangle({
        from: startDragPoint,
        to: event.point,
        strokeColor: 'red',
        strokeWidth: 1,
        guide: true,
      });

      // Stop showing the selected area on drag (new one is created) and up because we're done
      showSelection.removeOn({
        drag: true,
        up: true,
      });
    }
  };
}

export function resetCanvasCenter() {
  paper.view.center = new paper.Point(canvas.width / 2, canvas.height / 2);
}

export function fitToPage(imageWidth: number, imageHeight: number) {
  if (paper.view != null && canvas != null) {
    // Constant representation of the ratio of the canvas size to the image size
    let scalingFactor;

    if (canvas.height / canvas.width > imageHeight / imageWidth) {
      // Landscape (max width, height auto)
      scalingFactor = canvas.width / imageWidth;
    } else {
      // Portrait (max height, width auto)
      scalingFactor = canvas.height / imageHeight;
    }

    // Fit image to page so whole thing is displayed
    // Dynamic representation of the zoom needed to return to viewing the whole image in the canvas
    const zoomFactor = scalingFactor / paper.view.zoom;

    // Reset the center point to the center of the canvas
    const canvasCenter = new paper.Point(canvas.width / 2, canvas.height / 2);
    paper.view.center = canvasCenter;

    // Zoom to fit the whole image in the canvas
    const [newZoom, offset] = changeZoom(paper.view.zoom, zoomFactor, canvasCenter, canvasCenter);
    paper.view.zoom = newZoom;
    paper.view.center = paper.view.center.add(offset!);

    paper.view.draw();
  }
}

export function resizeCanvas() {
  if (paper.view != null) {
    paper.view.viewSize = new paper.Size(canvas.width, canvas.height);
  }
}
